import React from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { ArrowLeft, LogOut, Home, MessageSquare, ChevronDown, Check, Wind } from 'lucide-react'
import { Button } from '@/components'
import { useLanguage } from '@/contexts/LanguageContext'
import deepWindLogo from '@/assets/deepwind-logo.png'
import { useTurbine360ViewModel } from './useTurbine360ViewModel'
import CommentSidebar from './components/CommentSidebar'
import RoomPhotoSidebar from './components/RoomPhotoSidebar'
import Enhanced360Viewer from './components/Enhanced360Viewer'

const Turbine360ViewScreen: React.FC = () => {
  const navigate = useNavigate()
  const { t } = useLanguage()

  const {
    turbine,
    currentRoom,
    setCurrentRoom,
    selectedBlade,
    setSelectedBlade,
    selectedPhoto,
    setSelectedPhoto,
    error,
    isSidebarOpen,
    isCommentsOpen,
    setIsCommentsOpen,
    loading,
    viewerRefreshKey,
    setViewerRefreshKey,
    photoVersions,
    currentVersionIndex,
    loadingVersions,
    isVersionMenuOpen,
    setIsVersionMenuOpen,
    versionDropdownRef,
    currentUser,
    getVersionLabel,
    formatVersionMeta,
    handleVersionChange,
    handleDeleteVersion,
    forceRefreshVersions,
    handleLogout,
  } = useTurbine360ViewModel()

  if (loading) {
    // Skeleton: panoramik görünüm düzenini (header + oda sidebar + viewer) taklit eder.
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header skeleton */}
        <div className="bg-white shadow-sm border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="h-16 flex items-center justify-between animate-pulse">
              <div className="flex items-center space-x-3">
                <div className="w-9 h-9 bg-gray-200 rounded-lg" />
                <div className="h-5 w-44 bg-gray-200 rounded" />
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-9 h-9 bg-gray-200 rounded-lg" />
                <div className="w-24 h-9 bg-gray-200 rounded-lg" />
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar + viewer skeleton */}
        <div className="flex h-[calc(100vh-4rem)] animate-pulse">
          <div className="hidden md:block w-80 bg-white border-r border-gray-200 p-4 space-y-3">
            <div className="h-6 w-32 bg-gray-200 rounded mb-4" />
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="h-12 bg-gray-200 rounded-lg" />
            ))}
          </div>
          <div className="flex-1 bg-gray-100 p-4">
            <div className="h-full min-h-[500px] bg-gray-200 rounded-xl" />
          </div>
        </div>
      </div>
    )
  }

  if (!loading && (error || !turbine)) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            {error || 'Türbin bulunamadı'}
          </h2>
          <Button onClick={() => navigate('/dashboard')} variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t.loading.goBack}
          </Button>
        </div>
      </div>
    )
  }

  if (!turbine) return null

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b relative z-[100]">
        {/* Full-width (max-w-7xl mx-auto kaldırıldı) → logo/başlık bloğu en solda. */}
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              {/* Logo + name — far left, navigates to the dashboard (home) */}
              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                className="flex items-center space-x-2 rounded-lg hover:opacity-80 transition-opacity focus:outline-none"
                title="Ana sayfaya dön"
              >
                <div className="w-8 h-8 rounded-lg flex items-center justify-center">
                  <img
                    src={deepWindLogo}
                    alt="VentraWind Logo"
                    className="w-8 h-8 object-contain"
                  />
                </div>
                <div className="text-left">
                  <h1 className="text-xl font-bold text-gray-900">VentraWind</h1>
                  <p className="text-xs text-gray-500">360° Türbin Görüntüleyici</p>
                </div>
              </button>

              {/* Return-to-home link — replaces the old "Türbin Yönetimi" button */}
              <Button
                onClick={() => navigate('/dashboard')}
                variant="outline"
                size="sm"
                className="hover:bg-blue-50 hover:border-blue-200 hover:text-blue-600 transition-colors"
              >
                <Home className="w-4 h-4 mr-2" />
                Ana sayfaya dön
              </Button>

              {/* Türbin adı — topbar chip'i */}
              <div className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-50 border border-blue-100">
                <Wind className="w-3.5 h-3.5 text-blue-500 shrink-0" />
                <span className="text-sm font-semibold text-blue-700 leading-none max-w-[220px] truncate">
                  {turbine.name || turbine.turbine_name || turbine.code}
                </span>
              </div>
            </div>
            
            {selectedPhoto && photoVersions.length > 0 && (
              <div className="flex items-center">
                <div className="relative" ref={versionDropdownRef}>
                  <button
                    type="button"
                    onClick={() => !loadingVersions && setIsVersionMenuOpen(prev => !prev)}
                    disabled={loadingVersions}
                    className={`flex items-center space-x-3 px-3 py-2 bg-white border border-gray-200 rounded-xl shadow-sm hover:border-gray-300 transition-colors ${
                      loadingVersions ? 'opacity-70 cursor-not-allowed' : ''
                    }`}
                  >
                    <div className="text-left">
                      <p className="text-[10px] uppercase tracking-[0.08em] text-gray-500 font-semibold leading-tight">
                        Versiyon
                      </p>
                      <p className="text-sm font-semibold text-gray-900 leading-tight">
                        {getVersionLabel(photoVersions[currentVersionIndex], currentVersionIndex)}
                      </p>
                    </div>
                    <ChevronDown
                      className={`w-4 h-4 text-gray-500 transition-transform ${isVersionMenuOpen ? 'rotate-180' : ''}`}
                    />
                  </button>

                  {isVersionMenuOpen && (
                    <div className="absolute right-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-xl z-50 overflow-hidden w-auto min-w-[280px] max-w-md">
                      <ul className="py-1 overflow-y-auto" style={{ maxHeight: '320px' }}>
                        {photoVersions.map((version, idx) => {
                          const isActive = idx === currentVersionIndex
                          const isSuperAdmin = currentUser?.role === 'superadmin'
                          const canDelete = isSuperAdmin && idx > 0 // Orijinal versiyonu silemesin

                          return (
                            <li key={`${version.id}-${idx}`}>
                              <div className={`flex items-start justify-between gap-3 px-3 py-2.5 ${
                                isActive ? 'bg-gray-100' : 'hover:bg-gray-50'
                              }`}>
                                <button
                                  type="button"
                                  className="flex-1 text-left"
                                  onClick={() => handleVersionChange(idx)}
                                >
                                  <div>
                                    <p className="text-sm font-semibold text-gray-900 leading-snug">
                                      {getVersionLabel(version, idx)}
                                    </p>
                                    {(version.created_at || version.created_by) && (
                                      <p className="text-xs text-gray-500 mt-0.5 leading-tight">
                                        {formatVersionMeta(version)}
                                      </p>
                                    )}
                                  </div>
                                </button>
                                <div className="flex items-center gap-2">
                                  {isActive && <Check className="w-4 h-4 text-green-600" />}
                                  {canDelete && (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        handleDeleteVersion(idx, version)
                                      }}
                                      className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                                      title="Bu versiyonu sil"
                                    >
                                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                      </svg>
                                    </button>
                                  )}
                                </div>
                              </div>
                            </li>
                          )
                        })}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            <div className="hidden lg:flex items-center space-x-3">
              <Button
                onClick={() => setIsCommentsOpen(!isCommentsOpen)}
                className="bg-green-500 hover:bg-green-600 text-white"
                size="sm"
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                Yorumlar
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="hover:bg-red-50 hover:border-red-200 hover:text-red-600 transition-colors"
                onClick={handleLogout}
              >
                <LogOut className="w-4 h-4 mr-2" />
                Çıkış
              </Button>
            </div>
          </div>
              </div>
      </header>

      {/* Main Content Area */}
      <div className="flex h-[calc(100vh-4rem)]">
        {/* Left Sidebar - Room Photo Controls */}
        <AnimatePresence>
          {isSidebarOpen && (
            <motion.div
              initial={{ x: -300, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: -300, opacity: 0 }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="w-80 bg-white shadow-xl border-r border-gray-200 z-10"
            >
              <RoomPhotoSidebar
                turbineId={turbine.id}
                totalRooms={11}
                selectedRoom={currentRoom}
                onRoomSelect={setCurrentRoom}
                selectedBlade={selectedBlade}
                onBladeSelect={setSelectedBlade}
                onPhotoSelect={setSelectedPhoto}
                selectedPhoto={selectedPhoto}
                isOpen={isSidebarOpen}
                currentUser={currentUser}
              />
            </motion.div>
          )}
        </AnimatePresence>
            
        {/* 360 Viewer Area */}
        <div className={`${isSidebarOpen ? 'flex-1' : 'w-full'} bg-gray-100 relative transition-all duration-300`}>
          <div className="h-full p-4">
            <div className="h-[calc(100vh-200px)] min-h-[500px] bg-white rounded-xl shadow-sm border overflow-hidden relative">
              <Enhanced360Viewer
                key={viewerRefreshKey}
                turbineId={turbine.id}
                roomIndex={currentRoom}
                selectedBlade={selectedBlade}
                selectedPhoto={selectedPhoto}
                onPhotoChange={setSelectedPhoto}
                isSidebarOpen={isSidebarOpen}
                onRefresh={() => setViewerRefreshKey(Date.now())}
                onVersionsRefresh={forceRefreshVersions}
                loadingVersions={loadingVersions}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Comments Sidebar */}
      <CommentSidebar
        turbineId={turbine.id}
        isOpen={isCommentsOpen}
        onClose={() => setIsCommentsOpen(false)}
      />

      {/* Bildirimler global NotificationProvider (toast) katmanından geliyor.
          Buradaki İKİ ayrı render (her zaman yeşil ToastNotification +
          tek slotluk NotificationModal) kaldırıldı: aynı olay için çift
          bildirim çıkıyor, hatalar da "başarılı" rengiyle gösteriliyordu. */}
    </div>
  )
}

export default Turbine360ViewScreen 
